/**
 * Gift card recipient form.
 *
 * Owns the fields rendered by snippets/gift-card-recipient-form.liquid on gift card
 * product pages. Loaded from sections/product.liquid only when product.gift_card? is true.
 *
 * Unbundled and vanilla, like assets/cart-drawer.js: assets/template.product.js is
 * compiled webpack output with no source in this repo (see README, "Assets & the missing
 * build step"), so nothing can be added to it.
 *
 * The whole job is a handover. Without JS the snippet ships a disabled checkbox and an
 * enabled hidden control with value `if_present`, which tells Shopify to validate the
 * recipient only if an email was actually typed. With JS we invert that: the checkbox
 * becomes the switch, and the fields are disabled unless it is ticked, so a self-purchase
 * posts no recipient properties at all.
 */
(function () {
	'use strict';

	var root = document.querySelector('[data-gift-card-recipient]');
	if (!root) return;

	var sectionId = root.getAttribute('data-section-id');

	function byId(prefix) {
		return document.getElementById(prefix + '-' + sectionId);
	}

	var checkbox = byId('Recipient-checkbox');
	var control = byId('Recipient-control');
	if (!checkbox || !control) return;

	var offset = byId('Recipient-timezone-offset');
	var email = byId('Recipient-email');
	var message = byId('Recipient-message');
	var inputs = [email, byId('Recipient-name'), message, byId('Recipient-send-on')].filter(Boolean);
	var errors = root.querySelector('[data-recipient-errors]');
	var counter = root.querySelector('[data-recipient-message-count]');
	var countTemplate = counter ? counter.getAttribute('data-recipient-count-template') || '' : '';

	/* -------------------------------------------------------------------- handover */

	// Until this class lands the stylesheet keeps the no-JS layout: checkbox hidden, fields
	// open. Adding it hides the fields again and hands control to the checkbox.
	root.classList.add('gift-card-recipient--js');

	checkbox.disabled = false;
	control.disabled = true;

	// Without this Shopify resolves a "Send on" date in the shop's timezone, not the buyer's.
	if (offset) offset.value = String(new Date().getTimezoneOffset());

	/* ---------------------------------------------------------------------- fields */

	function setDisabled(disabled) {
		inputs.forEach(function (input) {
			input.disabled = disabled;
		});
		if (offset) offset.disabled = disabled;

		// Applied from here rather than in the Liquid: the product form has no `novalidate`,
		// so this buys native validation for free, while a hardcoded `required` would block
		// the no-JS path from buying a card for yourself with the fields left blank.
		if (email) {
			if (disabled) {
				email.removeAttribute('required');
			} else {
				email.setAttribute('required', 'required');
			}
		}
	}

	function clearFields() {
		inputs.forEach(function (input) {
			input.value = '';
		});
		updateCount();
	}

	function clearErrors() {
		if (errors) errors.hidden = true;
		inputs.forEach(function (input) {
			input.removeAttribute('aria-invalid');
		});
	}

	function updateCount() {
		if (!counter || !message) return;
		counter.textContent = countTemplate.replace('__count__', String(message.value.length));
	}

	function onToggle() {
		root.classList.toggle('gift-card-recipient--open', checkbox.checked);
		if (checkbox.checked) {
			setDisabled(false);
			return;
		}
		clearFields();
		clearErrors();
		setDisabled(true);
	}

	checkbox.addEventListener('change', onToggle);
	if (message) message.addEventListener('input', updateCount);

	onToggle();
	updateCount();

	/* --------------------------------------------------------------------- reset */

	/**
	 * Fired by assets/cart-drawer.js only on a successful add from the product form. A
	 * failed add never reaches it, which is what we want: the buyer needs to see what they
	 * typed in order to fix it. Without this, a second gift card silently inherits the
	 * first recipient's email.
	 */
	document.addEventListener('cart:added', function (event) {
		if (!event.detail || event.detail.source !== 'product-form') return;
		if (!checkbox.checked) return;
		checkbox.checked = false;
		onToggle();
	});
})();
